import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GoogleAuthSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [debugInfo, setDebugInfo] = useState([]);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        setDebugInfo(prev => [...prev, '🚀 GoogleAuthSuccess montato']);
        console.log('🔍 [GoogleAuthSuccess] Inizio verifica autenticazione...');
        console.log('🔍 [GoogleAuthSuccess] API_URL:', API_URL);
        
        // Get token from URL query params (passed from backend callback)
        const tokenFromUrl = searchParams.get('token');
        console.log('🔍 [GoogleAuthSuccess] Token from URL:', tokenFromUrl ? 'PRESENTE' : 'ASSENTE');
        setDebugInfo(prev => [...prev, `🔑 Token da URL: ${tokenFromUrl ? 'PRESENTE' : 'ASSENTE'}`]);
        
        // Save token to localStorage for persistence
        if (tokenFromUrl) {
          localStorage.setItem('fantaclasse_session_token', tokenFromUrl);
          setDebugInfo(prev => [...prev, '💾 Session token salvato in localStorage']);
          console.log('✅ [GoogleAuthSuccess] Session token salvato in localStorage');
        }
        
        // Use token from URL or from localStorage
        const sessionToken = tokenFromUrl || localStorage.getItem('fantaclasse_session_token');
        
        if (!sessionToken) {
          throw new Error('Nessun token di sessione disponibile');
        }
        
        setDebugInfo(prev => [...prev, `📡 Chiamata a ${API_URL}/api/auth/me con token`]);
        
        // Call /api/auth/me with session token in header as fallback
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
          headers: {
            'X-Session-Token': sessionToken
          }
        });

        setDebugInfo(prev => [...prev, `📨 Response status: ${response.status}`]);
        console.log('🔍 [GoogleAuthSuccess] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          setDebugInfo(prev => [...prev, `❌ ERRORE: ${errorText}`]);
          console.error('❌ [GoogleAuthSuccess] Errore risposta:', errorText);
          throw new Error('Autenticazione fallita');
        }

        const userData = await response.json();
        setDebugInfo(prev => [...prev, `✅ Utente ricevuto: ${userData.name} (${userData.email})`]);
        console.log('✅ [GoogleAuthSuccess] Dati utente ricevuti:', userData);
        
        // Save user data to localStorage
        localStorage.setItem('fantaclasse_user', JSON.stringify(userData));
        setDebugInfo(prev => [...prev, '💾 Dati utente salvati in localStorage']);
        console.log('✅ [GoogleAuthSuccess] Dati salvati in localStorage');

        // Redirect to root using full page reload to reinitialize AuthContext
        // This ensures the app state is fully refreshed with the new user data
        // The Landing page will then handle the redirect to /leagues based on auth state
        setDebugInfo(prev => [...prev, '🔄 Redirect alla root (/) con full reload tra 500ms...']);
        console.log('🔄 [GoogleAuthSuccess] Redirect alla root con full page reload');
        
        setTimeout(() => {
          setDebugInfo(prev => [...prev, '🚀 ESEGUO FULL PAGE RELOAD VERSO ROOT!']);
          console.log('🔄 [GoogleAuthSuccess] Eseguo full page reload verso /');
          window.location.href = '/';
        }, 500);
      } catch (error) {
        setDebugInfo(prev => [...prev, `💥 ERRORE CRITICO: ${error.message}`]);
        console.error('❌ [GoogleAuthSuccess] Errore verifica autenticazione:', error);
        // If verification fails, redirect to login
        localStorage.removeItem('fantaclasse_user');
        localStorage.removeItem('fantaclasse_session_token');
        setDebugInfo(prev => [...prev, '🔄 Redirect a /login per errore tra 2 secondi']);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    console.log('🚀 [GoogleAuthSuccess] Componente montato, avvio verifyAuth...');
    verifyAuth();
  }, [navigate, setUser, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="text-center space-y-4 max-w-2xl w-full">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Accesso in corso con Google...
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Verifica dell'autenticazione Google
        </p>
        
        {/* DEBUG INFO - VISIBILE */}
        <div className="mt-6 p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-4 border-yellow-400 dark:border-yellow-600 text-left">
          <p className="font-bold text-xl mb-4 text-yellow-900 dark:text-yellow-100">
            🐛 DEBUG INFO (VISIBILE)
          </p>
          <div className="space-y-2 text-sm max-h-96 overflow-y-auto">
            {debugInfo.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400">Attendo informazioni...</p>
            ) : (
              debugInfo.map((info, index) => (
                <p key={index} className="text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-800 p-2 rounded">
                  {index + 1}. {info}
                </p>
              ))
            )}
          </div>
        </div>

        <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-left text-xs">
          <p className="font-semibold mb-2 text-gray-900 dark:text-white">🔍 Console Browser (F12):</p>
          <p className="text-gray-600 dark:text-gray-400">Apri la Console del Browser per vedere log aggiuntivi</p>
          <p className="text-red-600 dark:text-red-400 mt-2 font-bold">
            ⚠️ Se dopo 3 secondi non vieni reindirizzato, COPIA TUTTO il debug sopra e condividilo!
          </p>
        </div>
      </div>
    </div>
  );
};
